import { useState, useEffect, useRef } from 'react';

// ── Shared AudioContext (lazy, singleton) ────────────────────────────────
let _sharedCtx = null;
function getAudioCtx() {
  if (!_sharedCtx) {
    _sharedCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (_sharedCtx.state === 'suspended') _sharedCtx.resume();
  return _sharedCtx;
}

// ── In-memory waveform cache { url -> Float32Array } ────────────────────
const waveformCache = new Map();

/**
 * useAudioWaveform(url, { bars = 80, b64 = null })
 *
 * Returns { peaks: Float32Array | null, loading: boolean, error: string | null }
 *
 * peaks[i] ∈ [0, 1] — RMS amplitude for bar i
 * Pass `b64` (data URL) to bypass fetch (for local cached tracks).
 */
export function useAudioWaveform(url, { bars = 80, b64 = null } = {}) {
  const [peaks,   setPeaks]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const source = b64 || url;
    if (!source) { setPeaks(null); return; }

    // Cache hit
    const cacheKey = `${source}::${bars}`;
    if (waveformCache.has(cacheKey)) {
      setPeaks(waveformCache.get(cacheKey));
      return;
    }

    let cancelled = false;
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError(null);

    async function run() {
      try {
        let arrayBuffer;

        if (b64) {
          // Decode base64 data URL
          const [, data] = b64.split(',');
          const bin = atob(data);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          arrayBuffer = bytes.buffer;
        } else {
          const resp = await fetch(url, { signal: ctrl.signal });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          arrayBuffer = await resp.arrayBuffer();
        }

        if (cancelled) return;

        const ctx = getAudioCtx();
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));

        if (cancelled) return;

        // Mix down to mono
        const numChannels = audioBuffer.numberOfChannels;
        const length = audioBuffer.length;
        const mono = new Float32Array(length);
        for (let c = 0; c < numChannels; c++) {
          const ch = audioBuffer.getChannelData(c);
          for (let i = 0; i < length; i++) mono[i] += ch[i];
        }
        if (numChannels > 1) {
          for (let i = 0; i < length; i++) mono[i] /= numChannels;
        }

        // Downsample to `bars` RMS peaks
        const chunkSize = Math.floor(length / bars);
        const peaks = new Float32Array(bars);
        for (let b = 0; b < bars; b++) {
          const start = b * chunkSize;
          const end = start + chunkSize;
          let sum = 0;
          for (let i = start; i < end; i++) sum += mono[i] * mono[i];
          peaks[b] = Math.sqrt(sum / chunkSize);
        }

        // Normalize [0, 1]
        let max = 0;
        for (let i = 0; i < peaks.length; i++) if (peaks[i] > max) max = peaks[i];
        if (max > 0) for (let i = 0; i < peaks.length; i++) peaks[i] /= max;

        // Floor small values for visual clarity
        for (let i = 0; i < peaks.length; i++) peaks[i] = Math.max(0.04, peaks[i]);

        waveformCache.set(cacheKey, peaks);
        if (!cancelled) setPeaks(peaks);
      } catch (err) {
        if (cancelled || err.name === 'AbortError') return;
        console.warn('[useAudioWaveform]', err);
        setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [url, b64, bars]);

  return { peaks, loading, error };
}

/**
 * drawWaveform(canvas, peaks, progress, colorPlayed, colorUnplayed)
 *
 * Draws a bar-style waveform onto a canvas element.
 * progress ∈ [0, 1] — portion already played (colored differently)
 */
export function drawWaveform(canvas, peaks, progress = 0, colorPlayed = '#FF7900', colorUnplayed = 'rgba(255,121,0,0.25)') {
  if (!canvas || !peaks) return;
  const ctx = canvas.getContext('2d');
  const W = canvas.width;
  const H = canvas.height;
  ctx.clearRect(0, 0, W, H);

  const n = peaks.length;
  const gap = 1.5;
  const barW = Math.max(1, (W - gap * (n - 1)) / n);

  for (let i = 0; i < n; i++) {
    const x = i * (barW + gap);
    const barH = Math.max(2, peaks[i] * H);
    const y = (H - barH) / 2;
    const played = i / n <= progress;
    ctx.fillStyle = played ? colorPlayed : colorUnplayed;
    ctx.beginPath();
    // Rounded bars
    const r = Math.min(barW / 2, 2);
    ctx.roundRect(x, y, barW, barH, r);
    ctx.fill();
  }
}
